import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { tokenStore } from '../../auth/tokenStore';
import { authEvents } from '../../auth/authEvents';
import { newCorrelationId } from './correlationId';

/*
 * Axios client — talks DIRECTLY to crs-api (no BFF), exactly as crs-web does.
 * Every request carries Authorization, X-Tenant-ID, X-App-ID, X-Correlation-ID
 * and X-Timezone. A 401 triggers one single-flight refresh through
 * POST /api/v1/Auth/refresh and a retry; a failed refresh clears the tokens
 * and emits 'cleared' so SessionProvider drops to /login.
 */

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const REFRESH_PATH = '/api/v1/Auth/refresh';

let refreshInFlight: Promise<string | null> | null = null;

async function singleFlightRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) return null;

      const tenantId = tokenStore.getTenantId();
      const applicationId = tokenStore.getAppId();

      const headers: Record<string, string> = {
        'X-Correlation-ID': newCorrelationId(),
      };
      if (tenantId) headers['X-Tenant-ID'] = tenantId;
      if (applicationId) headers['X-App-ID'] = applicationId;

      // Plain axios on purpose: the refresh call must not loop through the
      // interceptor below.
      const res = await axios.post<unknown>(
        `${import.meta.env.VITE_API_BASE_URL}${REFRESH_PATH}`,
        {
          refreshToken,
          tenantId: tenantId ?? undefined,
          applicationId: applicationId ?? undefined,
        },
        { headers },
      );

      // TenantHub shape: { success, data: { accessToken, refreshToken, ... } };
      // tolerate a flat body too.
      const body = res.data as { success?: boolean; data?: unknown } & Record<string, unknown>;
      const tokenData = (body.data ?? body) as {
        accessToken?: string;
        token?: string;
        refreshToken?: string;
      };
      const newAccess = tokenData.accessToken ?? tokenData.token;
      if (!newAccess) return null;

      tokenStore.setAccessToken(newAccess);
      if (tokenData.refreshToken) tokenStore.setRefreshToken(tokenData.refreshToken);
      return newAccess;
    } catch {
      tokenStore.clear();
      authEvents.emit('cleared');
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function isAuthUrl(url: string): boolean {
  return /\/api\/v1\/Auth\//i.test(url);
}

/**
 * The API refused the request's outlet: a 400 OUTLET_REQUIRED ("Choose an
 * outlet…") or a 403 naming the outlet ("You are not assigned to that
 * outlet…", "You have no outlet access…"). See crs-api DineOpsContext.
 */
export function isOutletRefusal(status: number | undefined, body: unknown): boolean {
  if (status !== 400 && status !== 403) return false;
  const b = (body ?? {}) as { message?: unknown; errors?: unknown };
  const texts = [typeof b.message === 'string' ? b.message : '', ...(Array.isArray(b.errors) ? b.errors.filter((e): e is string => typeof e === 'string') : [])];
  if (status === 400) return texts.some((t) => t.includes('[OUTLET_REQUIRED]') || /choose an outlet/i.test(t));
  // Only DineOpsContext's two outlet refusals; other 403s that merely mention
  // an outlet (a department not linked to it, say) must not drop the outlet.
  return texts.some((t) => /^You are not assigned to that outlet\b|^You have no outlet access\b/i.test(t));
}

function isMyOutletsUrl(url: string): boolean {
  return /\/outlets\/mine/i.test(url);
}

export function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 15_000,
  });

  instance.interceptors.request.use((config) => {
    const at = tokenStore.getAccessToken();
    if (at) config.headers.set('Authorization', `Bearer ${at}`);

    const tenantId = tokenStore.getTenantId();
    if (tenantId && !config.headers.get('X-Tenant-ID')) {
      config.headers.set('X-Tenant-ID', tenantId);
    }

    // X-App-ID is pinned to the env-configured client ID for the whole
    // session (see authService.login for why it never drifts).
    const appId = tokenStore.getAppId();
    if (appId) config.headers.set('X-App-ID', appId);

    // Multi-outlet: the outlet picked at sign-in; the API checks it against the waiter's outlets.
    const outletId = tokenStore.getOutletId();
    if (outletId && !config.headers.get('X-Outlet-ID')) config.headers.set('X-Outlet-ID', outletId);

    if (!config.headers.get('X-Correlation-ID')) {
      config.headers.set('X-Correlation-ID', newCorrelationId());
    }
    config.headers.set('X-Timezone', browserTimezone());
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const url = original?.url ?? '';

      if (
        error.response?.status === 401 &&
        original &&
        !original._retry &&
        !isAuthUrl(url)
      ) {
        original._retry = true;
        const newToken = await singleFlightRefresh();
        if (newToken) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return instance.request(original);
        }
        // Refresh failed: tokens cleared + 'cleared' emitted already.
      }

      // The outlet is no longer this waiter's (or was never chosen): drop it
      // and its section, and let OutletGate ask again.
      if (!isAuthUrl(url) && !isMyOutletsUrl(url) && tokenStore.getAccessToken() && isOutletRefusal(error.response?.status, error.response?.data)) {
        tokenStore.setOutletId(null);
        tokenStore.setSection(null);
        authEvents.emit('outletCleared');
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

export const apiClient = createApiClient();
